import React from "react";
import { FaEnvelope, FaFilePdf, FaSearch, FaFilter, FaChartLine, FaUserTie, FaBell } from "react-icons/fa";

const Supports = () => {
  return (
    <div className="p-8 max-w-6xl mx-auto space-y-12">
      {/* Header */}
      <header className="text-center space-y-3">
        <h1 className="text-4xl font-bold text-color1">Support & Documentation</h1>
        <p className="text-color5">
          Bienvenue dans l’espace d’aide. Pour toute question, contactez-nous à{" "}
          <a href="mailto:contact@support.fr" className="text-color3 font-semibold hover:underline">
            contact@support.fr
          </a>.
        </p>
      </header>

      {/* Dashboard */}
      <section className="bg-white border border-color13 rounded-2xl shadow-lg p-8">
        <h2 className="text-2xl font-semibold text-color1 mb-6 flex items-center gap-2">
          <FaChartLine /> Dashboard & Statistiques
        </h2>
        <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-color6">
          <li className="p-5 border rounded-xl bg-gray-50 shadow-sm hover:shadow-md transition">
            📈 Revenu par année avec graphique interactif.
          </li>
          <li className="p-5 border rounded-xl bg-gray-50 shadow-sm hover:shadow-md transition">
            ⏳ Liste des factures en retard pour relance rapide.
          </li>
          <li className="p-5 border rounded-xl bg-gray-50 shadow-sm hover:shadow-md transition">
            🏆 Top 3 des clients les plus actifs.
          </li>
        </ul>
      </section>

      {/* Fonctionnalités clés */}
      <section className="grid md:grid-cols-2 gap-6">
        {/* Devis */}
        <div className="bg-white border border-color13 rounded-xl shadow-lg p-6 space-y-3 hover:shadow-xl transition">
          <h2 className="text-xl font-semibold text-color1">Devis</h2>
          <p>- Chaque devis reçoit un numéro unique (ex: DEV-0001).</p>
          <p>- Un devis peut être <strong>transformé en facture unique</strong>.</p>
          <p className="flex items-center gap-2">
            - Téléchargement PDF disponible{" "}
            <button className="flex items-center gap-2 border px-3 py-1 rounded text-sm bg-gray-100 hover:bg-gray-200">
              <FaFilePdf className="text-red-500" /> Générer PDF
            </button>
          </p>
          <p>
            - Filtrer les devis par statut :{" "}
            <code>brouillon, envoyé, accepté, refusé, expiré, transformé</code>.
          </p>
        </div>

        {/* Clients */}
        <div className="bg-white border border-color13 rounded-xl shadow-lg p-6 space-y-3 hover:shadow-xl transition">
          <h2 className="text-xl font-semibold text-color1">Clients</h2>
          <p>- Chaque client possède un <strong>code unique</strong>.</p>
          <p className="flex items-center gap-2">
            - Recherche possible uniquement par <strong>nom client</strong>{" "}
            <FaSearch className="text-color3" />
          </p>
        </div>

        {/* Factures */}
        <div className="bg-white border border-color13 rounded-xl shadow-lg p-6 space-y-3 hover:shadow-xl transition">
          <h2 className="text-xl font-semibold text-color1">Factures</h2>
          <p>- Chaque facture possède un numéro unique (ex: FAC-0001).</p>
          <p className="flex items-center gap-2">
            - Génération PDF disponible{" "}
            <button className="flex items-center gap-2 border px-3 py-1 rounded text-sm bg-gray-100 hover:bg-gray-200">
              <FaFilePdf className="text-red-500" /> PDF
            </button>
          </p>
          <p>
            - Filtrer les factures par statut :{" "}
            <code>brouillon, envoyé, accepté, refusé, expiré</code>.
          </p>
        </div>

        {/* Filtres & Relances */}
        <div className="bg-white border border-color13 rounded-xl shadow-lg p-6 space-y-3 hover:shadow-xl transition">
          <h2 className="text-xl font-semibold text-color1">Filtres & Relances</h2>
          <p>- Filtrage intelligent par statut de devis ou facture.</p>
          <p>- Recherche rapide par nom de client ou numéro.</p>
          <p className="flex items-center gap-2">
            <FaBell className="text-yellow-500" /> Système de relance automatique :
          </p>
          <ul className="pl-6 list-disc text-color6">
            <li>1ère et 2ème relance envoyées par email.</li>
            <li>À la 3ème relance → le client reçoit une <strong>mise en demeure</strong>.</li>
          </ul>
          <button className="flex items-center gap-2 border px-3 py-2 rounded bg-gray-100 hover:bg-gray-200">
            <FaFilter /> Essayer les filtres
          </button>
        </div>
      </section>

      {/* Restrictions inscription & Profil */}
      <section className="bg-white border border-color13 rounded-2xl shadow-lg p-8">
        <h2 className="text-2xl font-semibold text-color1 mb-4 flex items-center gap-2">
          <FaUserTie /> Gestion du Profil & Abonnement
        </h2>
        <ul className="space-y-2 text-color6">
          <li>- Après inscription, vous devez compléter votre profil : logo, nom commercial, statut juridique.</li>
          <li>- Le numéro <strong>SIRET</strong> est <u>obligatoire et unique</u>.</li>
          <li>- L’email est <u>obligatoire et unique</u>.</li>
          <li>- <strong>Nom complet, email et SIRET</strong> ne sont pas modifiables.</li>
          <li>- Par défaut : <strong>3 devis gratuits</strong>.</li>
          <li>- Pour ajouter/modifier/supprimer plusieurs devis et factures : passer au plan <span className="font-semibold text-color3">Premium</span>.</li>
        </ul>
      </section>

      {/* FAQ */}
      <section>
        <h2 className="text-2xl font-semibold mb-4 text-color1">Questions fréquentes</h2>
        <div className="space-y-3">
          <details className="bg-white rounded-lg shadow p-4">
            <summary className="cursor-pointer font-semibold">
              Comment transformer un devis en facture ?
            </summary>
            <p className="mt-2 text-color6">
              Depuis la page Devis, cliquez sur <strong>"Transformer en facture"</strong>.
              Une facture unique est générée automatiquement.
            </p>
          </details>
          <details className="bg-white rounded-lg shadow p-4">
            <summary className="cursor-pointer font-semibold">
              Puis-je télécharger mes documents en PDF ?
            </summary>
            <p className="mt-2 text-color6">
              Oui, chaque devis et facture possède un bouton <strong>PDF</strong> pour télécharger le document.
            </p>
          </details>
          <details className="bg-white rounded-lg shadow p-4">
            <summary className="cursor-pointer font-semibold">
              Comment fonctionne la relance automatique des factures ?
            </summary>
            <p className="mt-2 text-color6">
              Les clients reçoivent jusqu’à 3 relances automatiques. À la 3ème, une{" "}
              <strong>mise en demeure</strong> leur est envoyée.
            </p>
          </details>
        </div>
      </section>

      {/* Contact */}
      <section className="text-center space-y-2">
        <p className="text-color6">Vous ne trouvez pas votre réponse ?</p>
        <a
          href="mailto:contact@support.fr"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-color3 text-white hover:bg-color1 transition"
        >
          <FaEnvelope /> Contacter le support
        </a>
      </section>
    </div>
  );
};

export default Supports;
